// Aura "Lasts N of your turns" lifespan tracker.
//
// Sorcery wording: a card that says "Lasts 3 of your turns" persists
// through its controller's next THREE end-of-turn phases, then dispels.
// Implementation: stamp a per-card countdown on play; at the END of
// each of the controller's turns (after `fire_on_turn_end` resolves
// so the card's own end-of-turn triggers get to fire on their final
// turn), decrement; dispel when the counter reaches 0.
//
// Pure. No GameBoard state. The caller wires:
//   1. The `bind_turn_lifespan` op handler calls `bindTurnLifespan` on
//      play (stamps `card._lifespanRemaining = turns`).
//   2. The `expire_turn_lifespans` turn step calls
//      `tickLifespansAtEndOfTurn` and dispels the returned list.

/**
 * Stamp a controller-turn-counted lifespan on the source card. Idempotent
 * for the same card. Call sites firing `on_play` more than once (rare)
 * always overwrite with the same N so the counter doesn't drift.
 *
 * @param {object} sourceCard
 * @param {number} turns - integer ≥ 1
 */
export function bindTurnLifespan(sourceCard, turns) {
  if (!sourceCard) return;
  if (typeof turns !== 'number' || turns <= 0) return;
  sourceCard._lifespanRemaining = turns;
}

/**
 * Decrement every in-play card's lifespan counter for the given
 * controller's end-of-turn. Returns the list of cards whose counter
 * just reached 0. Caller dispels them.
 *
 * The mutation happens in-place on the card instance; idempotent if
 * the same card is passed through more than once in a turn (the second
 * pass would over-decrement, which the caller must avoid by calling
 * this once per turn-end).
 *
 * @param {Iterable<[any, { userData: { cardInstance } }]>} meshes
 * @param {'p1'|'p2'} controller
 * @returns {object[]} cards that should dispel now
 */
export function tickLifespansAtEndOfTurn(meshes, controller) {
  const controllerRotated = controller === 'p2';
  const expired = [];
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    if (!!card.rotated !== controllerRotated) continue;
    if (typeof card._lifespanRemaining !== 'number') continue;
    card._lifespanRemaining -= 1;
    if (card._lifespanRemaining <= 0) expired.push(card);
  }
  return expired;
}
